/**
 * Osquery Settings – configure automated Osquery collection for Go Agent.
 * Stored in backend osquery_settings table; delivered via agent-config.
 */
import React, { useState, useEffect } from 'react';
import { Database, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import OsqueryQueryLibrary from './OsqueryQueryLibrary';

// The query list itself now lives in the osquery_queries catalog, edited through
// OsqueryQueryLibrary. This form only carries the collection knobs that still
// live in osquery_settings.
interface OsquerySettingsForm {
  enabled: boolean;
  interval_minutes: number;
  target_platforms: string[];
  server_only: boolean;
}

const DEFAULT_SETTINGS: OsquerySettingsForm = {
  enabled: false,
  interval_minutes: 15,
  target_platforms: ['linux', 'windows'],
  server_only: true,
};

const OsquerySettings: React.FC = () => {
  const [settings, setSettings] = useState<OsquerySettingsForm>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [telemetryStatus, setTelemetryStatus] = useState<{
    snapshot_count_last_24h: number;
    device_ids_with_snapshots_last_24h: string[];
  } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (!loading && settings.enabled) {
      apiService.getOsqueryTelemetryStatus().then((s) => {
        setTelemetryStatus({
          snapshot_count_last_24h: s.snapshot_count_last_24h,
          device_ids_with_snapshots_last_24h: s.device_ids_with_snapshots_last_24h || [],
        });
      }).catch(() => setTelemetryStatus(null));
    } else {
      setTelemetryStatus(null);
    }
  }, [loading, settings.enabled]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getOsquerySettings();
      if (res?.success && res?.data?.settings) {
        const s = res.data.settings;
        setSettings({
          enabled: Boolean(s.enabled),
          interval_minutes: Number(s.interval_minutes) || 15,
          target_platforms: Array.isArray(s.target_platforms) ? s.target_platforms : ['linux', 'windows'],
          server_only: Boolean(s.server_only ?? true),
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : typeof err === 'object' && err !== null && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to load Osquery settings';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (updates: Partial<OsquerySettingsForm>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
    setSaved(false);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiService.updateOsquerySettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : typeof err === 'object' && err !== null && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to save settings';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-600">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-blue-600" />
        Loading Osquery settings...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Database size={20} className="text-blue-600" />
          Osquery Collection
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Configure automated Osquery queries for Go Agent. When enabled, agents run these queries on schedule and send results to the UEM server for compliance and visibility.
        </p>
      </div>

      {saved && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <CheckCircle size={18} />
          Settings saved. Agents will receive updated config on next poll.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {settings.enabled && telemetryStatus !== null && (
        <div className={`p-4 rounded-lg border text-sm max-w-2xl ${telemetryStatus.snapshot_count_last_24h > 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          <span className="font-medium">Data received (last 24h): </span>
          {telemetryStatus.snapshot_count_last_24h > 0 ? (
            <>
              {telemetryStatus.snapshot_count_last_24h} snapshots from {telemetryStatus.device_ids_with_snapshots_last_24h.length} device(s).
              Health by Collection will show &quot;Metrics: Osquery&quot; and CPU/RAM/Storage from these devices.
            </>
          ) : (
            <>
              No snapshots yet. To see Osquery data in Health by Collection: use an agent build that includes the Osquery collector (e.g. Old_AgentGo from this repo); ensure <code className="bg-white/60 px-1 rounded">osqueryi</code> is installed on the device; the agent must use the same <code className="bg-white/60 px-1 rounded">device_id</code> (UUID) as in the UEM devices list when posting to <code className="bg-white/60 px-1 rounded">/api/v1/telemetry/&#123;device_id&#125;/osquery-snapshot</code>.
            </>
          )}
        </div>
      )}

      <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 space-y-6 max-w-2xl">
        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium text-slate-800">Enable Osquery collection</label>
            <p className="text-sm text-slate-500">Run Osquery on agents and send results to UEM</p>
          </div>
          <button
            type="button"
            onClick={() => handleChange({ enabled: !settings.enabled })}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              settings.enabled ? 'bg-blue-600' : 'bg-slate-200'
            }`}
            role="switch"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                settings.enabled ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div>
          <label className="block font-medium text-slate-800 mb-2">Default interval (minutes)</label>
          <p className="text-sm text-slate-500 mb-2">
            How often to run queries when query-specific interval is not set (default: 15)
          </p>
          <input
            type="number"
            min={5}
            max={1440}
            value={settings.interval_minutes}
            onChange={(e) => handleChange({ interval_minutes: parseInt(e.target.value, 10) || 15 })}
            className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block font-medium text-slate-800 mb-2">Target platforms</label>
          <p className="text-sm text-slate-500 mb-2">
            Platforms where Osquery will run (linux, windows, darwin)
          </p>
          <div className="flex flex-wrap gap-2">
            {['linux', 'windows', 'darwin'].map((p) => (
              <label key={p} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.target_platforms.includes(p)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...settings.target_platforms, p]
                      : settings.target_platforms.filter((x) => x !== p);
                    handleChange({ target_platforms: next });
                  }}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">{p}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium text-slate-800">Server devices only</label>
            <p className="text-sm text-slate-500">Run Osquery only on devices marked as server type</p>
          </div>
          <button
            type="button"
            onClick={() => handleChange({ server_only: !settings.server_only })}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              settings.server_only ? 'bg-blue-600' : 'bg-slate-200'
            }`}
            role="switch"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                settings.server_only ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="pt-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </div>
      </div>

      <div className="bg-slate-50 rounded-lg border border-slate-200 p-6">
        <OsqueryQueryLibrary />
        <p className="text-xs text-slate-400 mt-4">
          Results are sent to /api/v1/telemetry/{'{device_id}'}/osquery-snapshot. Keep queries
          lightweight — they run on every matching endpoint on the schedule set above.
        </p>
      </div>

      <p className="text-xs text-slate-400">
        Osquery must be installed on target devices. Go Agent uses osqueryi (one-shot) to run queries and POST results to the telemetry endpoint.
      </p>
    </div>
  );
};

export default OsquerySettings;
